import * as React from "react";
import { darken } from "polished";
import {
	styled,
	Input,
	Tooltip,
	Button,
	Checkbox,
	InputGroup,
	EllipsisIcon,
	} from "@lib/components";
import {
	FaChevronRight,
	FaChevronDown,
	FaHistory,
	FaPlay,
} from "react-icons/fa";
import { Draggable } from "react-beautiful-dnd";
import { IoIosPause } from "react-icons/io";
import { PausedItemsCountTag } from "../menus/components/PausedItemsCountTag";
import { PauseTag } from "../menus/components/PauseTag";
import { ResumeModal } from "../menus/components/ResumeModal";
import { MENU_ITEM_TYPE } from "../menus/constant";
import { MenuCardActions, MenuCardActionsIconWrapper, MenuCardTitle, TextEllipsis } from ".";
import { IconCircleWrapper, IconCloud, IconCloudWrapper, IconCopyAlt, IconDelete, IconEdit, IconKiosk, IconKioskCloud, IconKioskCloudWrapper, IconMoreOptions, IconPrinterDisconnected } from "../../../../components/v2/icons";

const Wrapper = styled("div")``;

const Item = styled("div")<{ drag?: boolean; isPausing?: boolean; active?: boolean }>`
display: flex;
padding: 8px;
align-items: center;
gap: 12px;
align-self: stretch;
word-break: break-all;

border-radius: 4px;
background: var(--Neutral-White, #FEFEFE);

&.desktop-only {
 display: none;

    & > p {
        color: var(--Secondary-Smokey-Grey-1, #6B7270);
        font-family: Inter;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px; /* 142.857% */
    }
    @media (min-width: 768px) {
        display: flex;
    }
}

	border-bottom: ${({ isPausing }) => isPausing ? "1px solid #FB2F2F" : "1px solid transparent"}

`;

export const ItemButton = styled(Button)`
	color: ${({ theme }) => darken(0.6, theme.box.background)};
	&:hover {
		color: ${({ theme }) => theme.box.text};
	}
`;
export const MoreActionMobileMenu = styled(Button)`
display: none;
align-items: center;
gap: 4px;
flex: 1 0 0;
align-self: stretch;
height: 24px;
background: none;
border: none;
&:hover {
	background: none;
	border: none;
	box-shadow: none;
}
@media (max-width: 768px) {
	padding: 0;
    &.show-button-mobile {
		display: flex;
	}
}
`;

const SubWrapper = styled("div")`
	display: flex;
	justify-content: flex-end;
	padding-top: 6px;
`;

const SubContent = styled("div")`
	width: calc(100% - 25px);
`;

const ButtonsGroupRight = styled("div")`
	display: flex;
	align-items: center;
`;

export const IconKioskWrapper = styled.div<{variant?: string}>`
display: flex;
width: 28px;
height: 28px;
padding: 4px;
flex-direction: column;
justify-content: center;
align-items: center;
gap: 10px;
border-radius: 99px;
background: rgba(0, 100, 200, 0.10);
cursor: pointer;
${props => props.variant === 'danger' ?
`background: rgba(251, 47, 47, 0.10);
`
: null}
`

interface MenuListItemProps {
	id: string;
	type: string;
	name: React.ReactNode;
	index: number;
	mode: "normal" | "reOrder" | "editPrices";
	onCopy?: (index: number) => void;
	onEdit?: (index: number) => void;
	onHistory?: (index: number) => void;
	onPause?: (index: number) => void;
	onRemove?: (index: number) => void;
	onClick?: (index: number) => void;
	onPriceEdit?: (price: number) => void;
	buttons?: React.ReactNode;
	sortControls?: React.ReactNode;
	children?: React.ReactNode;
	price?: number;
	currency?: T.Core.Business.BusinessCurrency & {
		step: string;
	};
	isPausing?: boolean;
	pauseDuration?: number;
	pauseStart?: number;
	totalItems?: number;
	totalPausedItems?: number;
	isPauseInfinity?: boolean;
	active?: string;
	isItem?: boolean;
	isMenu?: boolean;
	activeVisibility?: boolean;
	toggleActiveVisibility?: () => void;
	conditions?: T.Schema.Restaurant.Menu.Availability
	onMoreAction?: (index: number, type: string) => void;
	showUnassignedReceiptPrinterIcon?: boolean;
	onReceiptPrinterAssignClick?: (e: React.MouseEvent) => void;
	selectable?: boolean;
	selected?: boolean;
	onToggleSelect?: (id: string) => void;
}
interface MenuListItemState {
	resumeModalOpen: boolean;
}

const toolTipWidth = 60;

export class MenuListItemV2 extends React.PureComponent<
	MenuListItemProps,
	MenuListItemState
> {
	constructor(props: MenuListItemProps) {
		super(props);
		this.state = {
			resumeModalOpen: false
		};
		this.toggleActive = this.toggleActive.bind(this);
	}

	toggleActive() {
		if (this.props.onClick) {
			this.props.onClick(this.props.index);
		}
	}

	render() {
		const {
			id,
			type,
			name,
			index,
			onCopy,
			onEdit,
			onHistory,
			onPause,
			onRemove,
			onPriceEdit,
			children,
			buttons,
			sortControls,
			mode,
			price,
			currency,
			isPausing,
			pauseDuration,
			pauseStart,
			totalItems,
			totalPausedItems,
			isPauseInfinity,
			isItem,
			isMenu=false,
			activeVisibility,
			toggleActiveVisibility,
			conditions,
			onMoreAction,
			showUnassignedReceiptPrinterIcon,
			onReceiptPrinterAssignClick,
			selectable,
			selected,
			onToggleSelect,
		} = this.props;

		const { resumeModalOpen } = this.state;
		const isExpanded = this.props.active === id;

		const handleCloseResumeModal = () => {
			this.setState({
				resumeModalOpen: false
			});
		}
		const handleOpenResumeModal = () => {
			this.setState({
				resumeModalOpen: true
			})
		}

		return (
			<>
			<ResumeModal isOpen={resumeModalOpen} handleClose={handleCloseResumeModal} menuItemIds={[id]} />
			<Draggable
				draggableId={id}
				index={index}
				type={type}
				isDragDisabled={mode !== "reOrder"}
			>
				{(provided) => (
					<Wrapper
						id={id}
						className="list-item"
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
					>
						<Item
							onClick={this.toggleActive}
							drag={mode === "reOrder"}
							isPausing={isPausing && type === MENU_ITEM_TYPE.DISH}
							active={this.props.active === id}
						>
							<MenuCardTitle className="flex-line centered flex-grow" style={{
								wordBreak: "break-all",
								maxWidth: '100%'
							}}>
								{mode === "normal" && selectable && (
									<Checkbox
										id={`menu-list-select-${id}`}
										checked={!!selected}
										onChange={(e) => {
											e.stopPropagation();
											onToggleSelect && onToggleSelect(id);
										}}
									/>
								)}
								{!!children && !isItem &&
									(isExpanded ? (
										<div onClick={this.toggleActive}>
										<FaChevronDown className="" style={{
											width: 30
										}} />
										</div>

									) : (
										<div onClick={this.toggleActive}>
										<FaChevronRight className="" style={{
											width: 30
										}} />
										</div>

									))}
								<TextEllipsis>{name}</TextEllipsis>
							</MenuCardTitle>
							{mode === "normal" && ( <div style={{display: 'flex', gap: 16, alignItems: 'center'}}>
									{MENU_ITEM_TYPE.DISH === type &&
										showUnassignedReceiptPrinterIcon &&
										onReceiptPrinterAssignClick && (
										<Tooltip text="No printer assigned. Tap to set" width={200} position="top">
										<IconCircleWrapper
										onClick={(e) => {
											e.stopPropagation();
											onReceiptPrinterAssignClick(e);
										}}>
										<IconPrinterDisconnected />
									</IconCircleWrapper>
									</Tooltip>
									)}

									{conditions?.pos?.enabled && conditions.online?.enabled && (
										<IconKioskCloudWrapper
										onClick={(e) => {
											e.stopPropagation();
											toggleActiveVisibility ? toggleActiveVisibility() : null;
										}}>
											<IconKioskCloud />
										</IconKioskCloudWrapper>
									)}
									{conditions?.pos?.enabled && !conditions?.online?.enabled &&
									<IconKioskWrapper
										onClick={(e) => {
											e.stopPropagation();
											toggleActiveVisibility ? toggleActiveVisibility() : null;
											}}>
										<IconKiosk />
									</IconKioskWrapper> }
									{!conditions?.pos?.enabled && conditions?.online?.enabled &&
									<IconCloudWrapper
										onClick={(e) => {
											e.stopPropagation();
											toggleActiveVisibility ? toggleActiveVisibility() : null;
											}}>
										<IconCloud />
									</IconCloudWrapper>}
									{sortControls && (
										<div
											onClick={(e) => e.stopPropagation()}
											onMouseDown={(e) => e.stopPropagation()}
										>
											{sortControls}
										</div>
									)}
									<div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center'}}>
										{
											[MENU_ITEM_TYPE.MENU, MENU_ITEM_TYPE.CATEGORY].includes(type) && isPausing && <PausedItemsCountTag totalItems={totalItems} totalPausedItems={totalPausedItems} />
										}
										<PauseTag isPauseInfinity={isPauseInfinity} pauseMenu={isPausing} type={type} pauseDuration={pauseDuration} pauseStart={pauseStart} />
										{buttons}
										{onPause && isPausing && type === MENU_ITEM_TYPE.DISH && <Tooltip
											text="Resume"
											width={toolTipWidth}
											position="top"
										>
											<MenuCardActionsIconWrapper
												onClick={(e) => {
													e.stopPropagation();
													handleOpenResumeModal();
												}}
											>
												<FaPlay />
											</MenuCardActionsIconWrapper>
										</Tooltip>}
										{onPause && !isPausing && type === MENU_ITEM_TYPE.DISH && <Tooltip
											text="Pause"
											width={toolTipWidth}
											position="top"
										>
											<MenuCardActionsIconWrapper
												onClick={(e) => {
													e.stopPropagation();
													onPause(index);
												}}
											>
												<IoIosPause />
											</MenuCardActionsIconWrapper>
										</Tooltip>}
										{onEdit && (
											<MenuCardActionsIconWrapper
											className={!!isMenu ? "hide-mobile" : ""}
											onClick={(e) => {
												e.stopPropagation();
												onEdit(index);
											} }>
												<IconEdit />
											</MenuCardActionsIconWrapper>
										)}
										{onRemove && (
											<MenuCardActionsIconWrapper
											className={!!isMenu ? "hide-mobile" : ""}
											onClick={(e) => {
												e.stopPropagation();
												onRemove(index);
											} }>
											<IconDelete />
											</MenuCardActionsIconWrapper>
										)}
										{onCopy && (
											<MenuCardActionsIconWrapper
											className={!!isMenu ? "hide-mobile" : ""}
											onClick={(e) => {
												e.stopPropagation();
												onCopy(index);
											} }>
											<IconCopyAlt />
											</MenuCardActionsIconWrapper>
										)}
										{onHistory && (
											<Tooltip text="Version history" width={100} position="top">
												<MenuCardActionsIconWrapper
												className={!!isMenu ? "hide-mobile" : ""}
												onClick={(e) => {
													e.stopPropagation();
													onHistory(index);
												} }>
												<FaHistory />
												</MenuCardActionsIconWrapper>
											</Tooltip>
										)}

											<MoreActionMobileMenu
												className={!!isMenu ? "show-button-mobile" : ""}
												onClick={(e) => {
													e.stopPropagation();
													e.preventDefault();
													onMoreAction ? onMoreAction(index, type) : null;
												}}
											>
												<IconMoreOptions />
											</MoreActionMobileMenu>
									</div>
								</div>
							)}
							{mode === "editPrices" && type === "DISH" && (
								<div style={{position: 'relative'}}>

									<span className="font-semi-bold"
										style={{position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)'}}>
											{currency!.symbol}
										</span>
									<Input
										type="number"
										step={currency!.step}
										style={{
											maxWidth: "6.5rem",
											marginRight: "0.2rem",
											paddingLeft: 24,
											minWidth: "6.5rem"
										}}
										min={0}
										required={true}
										value={price}
										onChange={(e) => {
											e.stopPropagation();

											if (e.target.validity.valid ||
												e.target.value === "") {
												const newPrice = parseFloat(
													e.target.value
												);

												onPriceEdit!(newPrice || 0);
											}
										} } />
								</div>
							)}

						</Item>
						{children && isExpanded && (
							<SubWrapper>
								<SubContent>{children}</SubContent>
							</SubWrapper>
						)}
					</Wrapper>
				)}
			</Draggable>

			</>
		);
	}
}
